import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { PerksQuirksSentiment } from "./PerksQuirksSentiment";
import { QuirkIcon } from "../../icons/QuirkIcon";
import { PerkIcon } from "../../icons/PerkIcon";
import type {
  PerkQuirkSentimentValue,
  PerkQuirkSentimentRecord,
} from "../../../types";

interface PerkCardItem {
  id: string;
  name: string;
  description?: string;
  type: "perk" | "quirk";
}

interface PerkCardProps {
  item: PerkCardItem;
  onSentimentRated?: (
    perksQuirksId: string,
    sentiment: PerkQuirkSentimentValue,
  ) => void;
  active?: boolean;
  sentiments?: PerkQuirkSentimentRecord[];
  onSentimentChange?: (
    perksQuirksId: string,
    sentiment: PerkQuirkSentimentValue,
  ) => void;
}

const Container = styled(Box, {
  label: "PerkQuirkItemContainer",
})<{ active?: boolean }>(({ active }) => ({
  position: "relative",
  width: 457,
  height: 342,
  opacity: active ? 1 : 0.6,
  transition: "opacity 0.3s ease-in-out",
}));

const BackgroundCard = styled(Box, {
  label: "PerkQuirkItemBackgroundCard",
})(({ theme }) => ({
  borderRadius: theme.spacing(3),
  backgroundColor: "#0351ab",
  border: "1px solid #0351ab",
  position: "absolute",
  top: 25,
  left: 5,
  width: 457,
  height: 352,
  padding: "32px 20px",
  transform: "rotate(-15deg)",
}));

const ItemCard = styled(Box, {
  label: "PerkQuirkItemCard",
})(({ theme }) => ({
  borderRadius: theme.spacing(3),
  backgroundColor: "#fff",
  color: "#000",
  border: "1px solid #E5E7EB",
  position: "absolute",
  top: 0,
  left: 0,
  display: "flex",
  width: 457,
  height: 342,
  padding: "32px 20px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "16px",
  transform: "rotate(-7deg)",
}));

const ItemTitle = styled(Typography, {
  label: "PerkQuirkItemTitle",
})(() => ({
  flexGrow: 1,
  color: "#1C252E",
  textAlign: "center",
  textOverflow: "ellipsis",
  fontFamily: "Proxima Nova",
  fontSize: "24px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "36px",
  letterSpacing: "0px",
}));

export const PerkCard = ({
  item,
  onSentimentRated,
  active,
  sentiments = [],
  onSentimentChange,
}: PerkCardProps) => {
  const handleSentimentRated = (
    perksQuirksId: string,
    sentiment: PerkQuirkSentimentValue,
  ) => {
    if (onSentimentRated) {
      onSentimentRated(perksQuirksId, sentiment);
    }
  };

  return (
    <Container active={active}>
      <BackgroundCard />
      <ItemCard>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "2px",
            fontFamily: "Proxima Nova",
            fontSize: "24px",
            fontWeight: 400,
            lineHeight: "36px",
            letterSpacing: "0px",
          }}
        >
          {item.type === "perk" ? <PerkIcon /> : <QuirkIcon />}
          <span>{item.type === "perk" ? "PERK" : "QUIRK"}</span>
        </Box>

        <ItemTitle>{item.name}</ItemTitle>

        <PerksQuirksSentiment
          perkQuirk={item}
          reason={`User rates ${item.type}: ${item.name}`}
          onRated={handleSentimentRated}
          sentiments={sentiments}
          onSentimentChange={onSentimentChange}
        />
      </ItemCard>
    </Container>
  );
};
